import { useEffect, useRef } from "react";
import "./GridIntro.css";

const GRID = 46; // must match the background grid in index.css
const COLOR = "53, 214, 231";

// Timing (ms)
const DRAW_DUR = 1800; // draw all lines in, accelerating (slow → fast)
const PULSE_DUR = 850; // whole-grid flash
const FADE_DUR = 550; // canvas fades out as the CSS grid + text take over

/**
 * One-time intro: draws the grid line-by-line in random order (slow, then
 * faster), flashes the whole grid once, then fades out. Calls onComplete when
 * the pulse peaks — that's the cue for the resting grid + text to appear.
 * Skips entirely (calls onComplete immediately) under prefers-reduced-motion.
 */
export default function GridIntro({ onComplete }) {
  const canvasRef = useRef(null);
  const onDoneRef = useRef(onComplete);
  onDoneRef.current = onComplete;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas && canvas.getContext && canvas.getContext("2d");

    let firedDone = false;
    const fireDone = () => {
      if (!firedDone) {
        firedDone = true;
        if (onDoneRef.current) onDoneRef.current();
      }
    };

    if (!ctx) {
      fireDone();
      return undefined;
    }

    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      fireDone();
      return undefined;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Build all grid lines, then shuffle for a random draw order
    const lines = [];
    for (let x = 0; x <= width; x += GRID) lines.push({ type: "v", pos: x });
    for (let y = 0; y <= height; y += GRID) lines.push({ type: "h", pos: y });
    for (let i = lines.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [lines[i], lines[j]] = [lines[j], lines[i]];
    }
    const N = lines.length;

    const drawLine = (ln, alpha, glow) => {
      ctx.strokeStyle = `rgba(${COLOR}, ${alpha})`;
      ctx.lineWidth = 1;
      ctx.shadowBlur = glow;
      ctx.shadowColor = glow ? `rgba(${COLOR}, ${alpha})` : "transparent";
      ctx.beginPath();
      if (ln.type === "v") {
        ctx.moveTo(ln.pos, 0);
        ctx.lineTo(ln.pos, height);
      } else {
        ctx.moveTo(0, ln.pos);
        ctx.lineTo(width, ln.pos);
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
    };

    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = now - start;
      ctx.clearRect(0, 0, width, height);

      if (t < DRAW_DUR) {
        // accelerating reveal: quadratic so it starts slow and speeds up
        const p = t / DRAW_DUR;
        const revealed = Math.min(N, Math.floor(N * p * p));
        for (let i = 0; i < revealed; i++) drawLine(lines[i], 0.22, 0);
        if (revealed > 0) drawLine(lines[revealed - 1], 0.7, 8); // newest line glows
        raf = requestAnimationFrame(tick);
      } else if (t < DRAW_DUR + PULSE_DUR) {
        // full-grid pulse: rise fast to a peak, then fall
        const pt = (t - DRAW_DUR) / PULSE_DUR;
        const env = pt < 0.25 ? pt / 0.25 : 1 - (pt - 0.25) / 0.75;
        const alpha = 0.22 + env * 0.65;
        const glow = env * 16;
        for (let i = 0; i < N; i++) drawLine(lines[i], alpha, glow);
        if (pt >= 0.25) fireDone(); // hand off to CSS grid + text as the pulse peaks
        raf = requestAnimationFrame(tick);
      } else if (t < DRAW_DUR + PULSE_DUR + FADE_DUR) {
        // fade the whole intro canvas out (CSS resting grid is now taking over)
        const fp = (t - DRAW_DUR - PULSE_DUR) / FADE_DUR;
        ctx.globalAlpha = 1 - fp;
        for (let i = 0; i < N; i++) drawLine(lines[i], 0.22, 0);
        ctx.globalAlpha = 1;
        raf = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, width, height);
        fireDone();
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="grid-intro" aria-hidden="true" />;
}
